import { TranslateService } from '@ngx-translate/core';
import { AbstractValueAccessor } from './AbstractValueAccessor';
import {
  Input,
  Injector,
  Directive,
  Output,
  EventEmitter,
  SimpleChanges,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { ValidationBaseOptions } from '../../validators/ValidationBaseOptions';

interface InputValidationError {
  key: string;
  messageKey: string;
  messageArgs?: any;
}

@Directive()
export abstract class BsNgInputBaseComponent extends AbstractValueAccessor {
  @Input() labelText = '';

  @Input()
  labelTitle: string;

  @Input() placeholder = '';
  @Input() requiredErrorMessage = '';
  @Input() identifier = '';
  @Input() name = '';
  @Input() required: string;
  @Input() key = '';
  @Input() noLabel: boolean;
  @Input() readonly = false;
  @Input() disabled = false;
  @Input() helpText: string;
  @Input() moreThanDateErrorMessage: string;
  @Input() maxErrorMessage: string;
  @Input() patternErrorMessage: string;

  @Input() customErrorMessage: string;
  @Output() blur = new EventEmitter();

  @Input()
  templateOptions: InputTemplateOptions = { direction: 'default' };

  emiratesIdFormatErrorMessage: string;
  minLengthErrorMessage: any = '';
  maxLengthErrorMessage: any = '';
  notInErrorMessage: any = '';
  alreadyExistErrorMessage: string;
  notFoundErrorMessage: string;
  infoMessage: string;

  public get isShowErrors(): boolean {
    return (
      this.ngControl.touched &&
      !this.ngControl.valid &&
      !this.ngControl.disabled
    );
  }

  public get errors(): InputValidationError[] {
    if (this.ngControl.touched && this.ngControl.errors) {
      if (this.ngControl.errors.required) {
        // Return only the required error when it is present.
        return [this.buildValidationError('required')];
      }

      const keys = Object.keys(this.ngControl.errors);

      return keys.map((keyError) => {
        const validationError = this.ngControl.errors[keyError];

        if (this.hasValidationOptions(validationError)) {
          return this.buildOptions(validationError);
        }

        return this.buildValidationError(
          keyError,
          typeof validationError === 'object' ? validationError : undefined
        );
      });
    }

    return [];
  }

  constructor(private translate: TranslateService, injector: Injector) {
    super(injector);
    this.registerLanguageChange(translate);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['key']) {
      if (this.key) {
        this.translate
          .get(this.key)
          .subscribe((trs) => this.assignTranslations(trs));
      }
    }
  }

  registerLanguageChange(translate: TranslateService) {
    translate.onLangChange.subscribe((language) => {
      this.getTranslations();
    });
  }

  getTranslations() {
    if (this.key) {
      this.translate
        .get(this.key)
        .subscribe((trs) => this.assignTranslations(trs));
    }
  }

  protected assignTranslations(trs: any) {
    this.labelText = trs.labelText || this.labelText;
    this.labelTitle = trs.labelTitle || this.labelTitle;
    this.placeholder = trs.placeholder || '';
    this.requiredErrorMessage =
      trs.requiredErrorMessage || this.requiredErrorMessage;
    this.helpText = trs.helpText || this.helpText;
    this.customErrorMessage =
      trs.customErrorMessage || this.customErrorMessage || '';
    this.emiratesIdFormatErrorMessage =
      trs.emiratesIdFormatErrorMessage || '';
    this.minLengthErrorMessage = trs.minLengthErrorMessage || '';
    this.maxLengthErrorMessage = trs.maxLengthErrorMessage || '';
    this.notInErrorMessage = trs.notInErrorMessage || '';
    this.patternErrorMessage = trs.patternErrorMessage;
    this.alreadyExistErrorMessage = trs.alreadyExistErrorMessage || '';
    this.notFoundErrorMessage = trs.notFoundErrorMessage || '';
    this.infoMessage = trs.infoMessage || '';
  }

  get hasMinLengthError(): boolean {
    return (
      this.ngControl.control.touched &&
      this.ngControl.control.errors &&
      this.ngControl.control.errors.minlength
    );
  }

  get hasMaxLengthError(): boolean {
    return (
      this.ngControl.control.touched &&
      this.ngControl.control.errors &&
      this.ngControl.control.errors.maxlength
    );
  }

  get hasNotInError(): boolean {
    return (
      this.ngControl.control.touched &&
      this.ngControl.control.errors &&
      this.ngControl.control.errors.notIn
    );
  }

  get hasAlreadyExistError(): boolean {
    return (
      this.ngControl.control.touched &&
      this.ngControl.control.errors &&
      this.ngControl.control.errors.alreadyExist
    );
  }

  get hasNotFoundError(): boolean {
    return (
      this.ngControl.control.touched &&
      this.ngControl.control.errors &&
      this.ngControl.control.errors.notFound
    );
  }

  isValid() {
    return this.ngControl.control.touched && this.ngControl.control.valid;
  }

  hasRequiredError(): boolean {
    return (
      this.ngControl.control.touched &&
      this.ngControl.control.errors &&
      this.ngControl.control.errors.required
    );
  }

  get hasEmiratesIdError(): boolean {
    return (
      this.ngControl.control.touched &&
      this.ngControl.control.errors &&
      this.ngControl.control.errors.emiratesIdFormat
    );
  }

  public hasRequiredValidator() {
    return this.ngControl.control.hasValidator(Validators.required);
  }

  get hasPatternError(): boolean {
    return (
      this.ngControl.control.touched &&
      this.ngControl.control.errors &&
      this.ngControl.control.errors.pattern
    );
  }

  get touched() {
    return this.ngControl.control.touched;
  }

  ///used for reactive forms
  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  onBlur() {
    this.blur.emit({ new: this.value });
    this.onTouched();
  }

  public get isRequired(): boolean {
    return this.required == 'true' || this.hasRequiredValidator();
  }

  protected buildOptions(
    validationOptions: ValidationBaseOptions
  ): InputValidationError {
    return this.buildValidationError(
      validationOptions.key,
      validationOptions.messageArgs
    );
  }

  private buildValidationError(
    errorKey: string,
    messageArgs?: any
  ): InputValidationError {
    return {
      key: errorKey,
      messageKey: this.getValidationMessageKey(errorKey),
      messageArgs,
    };
  }

  private getValidationMessageKey(errorKey: string): string {
    const angularValidationMessageKeys: Record<string, string> = {
      minlength: 'minLengthErrorMessage',
      maxlength: 'maxLengthErrorMessage',
    };

    if (angularValidationMessageKeys[errorKey]) {
      return angularValidationMessageKeys[errorKey];
    }

    return errorKey.endsWith('Error')
      ? `${errorKey}Message`
      : `${errorKey}ErrorMessage`;
  }

  private hasValidationOptions(
    validationError: any
  ): validationError is ValidationBaseOptions {
    return (
      validationError != null &&
      typeof validationError === 'object' &&
      typeof validationError.key === 'string'
    );
  }

  public trackByMessageKey(
    index: number,
    item: InputValidationError
  ): string {
    return item.key;
  }
}

export class InputTemplateOptions {
  direction: 'horizontal' | 'default';
}
